/**
 * Shared PostHog runtime: single, idempotent init point.
 *
 * This is the ONLY place allowed to call posthog.init().
 * Both instrumentation-client.ts and the tracking scripts go through here.
 */

import type { PostHog } from "posthog-js"
import {
  isBloomreachUndefinedValueError,
  isIgnoredExceptionMessage,
  isPosthogRecorderSecurityError,
  isRscConnectionClosedError,
  DENIED_URL_PATTERNS,
  GTM_PATTERNS,
} from "../error-filters"
import { getTrackingCookies } from "../getTrackingCookies"
import {
  POSTHOG_BROWSER_API_HOST,
  POSTHOG_PROJECT_TOKEN,
  POSTHOG_UI_HOST,
} from "../posthogConfig"

// ── Singleton state ──────────────────────────────────────────────────

let clientPromise: Promise<PostHog> | null = null
let resolvedClient: PostHog | null = null
let posthogEnabled = false

// ── Precompiled exception-filter regexes (built once at module load) ──

const GTM_REGEXES = GTM_PATTERNS.map((p) => new RegExp(p, "i"))
const DENIED_REGEXES = DENIED_URL_PATTERNS.map((p) => new RegExp(p, "i"))
const ALL_FILTER_REGEXES = [...GTM_REGEXES, ...DENIED_REGEXES]

// ── App-level enablement ─────────────────────────────────────────────

export const enablePostHog = () => {
  posthogEnabled = true
  if (typeof window !== "undefined") {
    window.__posthogEnabled = true
  }
}

export const isPostHogEnabled = (): boolean =>
  posthogEnabled ||
  (typeof window !== "undefined" && window.__posthogEnabled === true)

// ── Public API ───────────────────────────────────────────────────────

/**
 * The single entry point for PostHog initialization.
 * Multiple callers never double-init: a module-level promise backs this.
 */
export const ensurePosthogClient = (): Promise<PostHog> => {
  if (clientPromise) {
    return clientPromise
  }

  if (typeof window === "undefined") {
    return Promise.reject(new Error("PostHog cannot init on the server"))
  }

  clientPromise = import("posthog-js").then(({ default: posthog }) => {
    // If already initialized (e.g., by another path), just return
    if (posthog.__loaded) {
      resolvedClient = posthog
      window.posthog = posthog
      // Mirror the `loaded` callback's signal: listeners attach on this
      // path too.
      window.dispatchEvent(new Event("posthog-ready"))
      return posthog
    }

    posthog.init(POSTHOG_PROJECT_TOKEN, {
      api_host: POSTHOG_BROWSER_API_HOST,
      ui_host: POSTHOG_UI_HOST,
      capture_pageview: false,
      // Set explicitly rather than inherited from the project's remote config:
      // error tracking is load-bearing, and a settings toggle in the PostHog UI
      // must not be able to silently turn it off.
      capture_exceptions: true,
      enable_heatmaps: true,
      disable_web_experiments: false,
      disable_surveys: true,
      // Don't let the recorder reach into cross-origin iframes (Stripe,
      // PayPal, YouTube). It can't access them anyway, and the attempt throws
      // a SecurityError that the before_send filter below has to discard. This
      // This is the default. Set it explicitly so the intent is documented.
      session_recording: {
        recordCrossOriginIframes: false,
      },
      before_send: (event) => {
        if (!event) {
          return null
        }
        if (event.event !== "$exception") {
          return event
        }
        const props = event.properties || {}
        const msg: string = (props["$exception_message"] as string) || ""

        // `$exception_message` already includes the type prefix, so no type
        // is passed here.
        if (isIgnoredExceptionMessage(msg)) {
          return null
        }

        const exList: Array<{
          type?: string
          value?: string
          stacktrace?: {
            frames?: Array<{ filename?: string }>
          }
        }> =
          (props["$exception_list"] as Array<{
            type?: string
            value?: string
            stacktrace?: {
              frames?: Array<{ filename?: string }>
            }
          }>) || []

        let rawTrace: string =
          (props["$exception_stack_trace_raw"] as string) || ""
        if (typeof rawTrace !== "string") {
          try {
            rawTrace = JSON.stringify(rawTrace)
          } catch {
            rawTrace = ""
          }
        }

        if (
          isBloomreachUndefinedValueError({
            message: msg,
            frames: exList.flatMap(
              (ex) =>
                ex?.stacktrace?.frames?.map((frame) => frame.filename) ?? []
            ),
            rawStack: rawTrace,
          })
        ) {
          return null
        }

        for (const ex of exList) {
          // `ex.value` is prefix-less; pass `ex.type` so prefixed ignore-list
          // entries (e.g. "TypeError: ...") still match.
          if (isIgnoredExceptionMessage(ex?.value, ex?.type)) {
            return null
          }

          if (
            isBloomreachUndefinedValueError({
              message: ex?.value || "",
              frames: ex?.stacktrace?.frames?.map((frame) => frame.filename),
              rawStack: rawTrace,
            })
          ) {
            return null
          }

          // Drop PostHog session recorder cross-origin iframe errors
          // (Stripe/PayPal/YouTube).
          if (
            isPosthogRecorderSecurityError({
              type: ex?.type,
              message: ex?.value || msg,
              frames: ex?.stacktrace?.frames?.map((frame) => frame.filename),
            })
          ) {
            return null
          }

          // Drop Next.js RSC/Flight client "Connection closed." stream aborts.
          // Client-only (scoped to the Flight client bundle frame) so any
          // shared server-side ignore list doesn't swallow real
          // connection-drop errors.
          if (
            isRscConnectionClosedError({
              message: ex?.value || msg,
              frames: ex?.stacktrace?.frames?.map((frame) => frame.filename),
              rawStack: rawTrace,
            })
          ) {
            return null
          }
        }

        // Drop exceptions where structured frame filenames match
        // GTM or denied URL patterns (covers cases where raw trace
        // is empty but structured frames are present)
        for (const ex of exList) {
          const frames = ex?.stacktrace?.frames
          if (!frames) {
            continue
          }
          for (const frame of frames) {
            const filename = frame.filename
            if (!filename) {
              continue
            }
            if (ALL_FILTER_REGEXES.some((re) => re.test(filename))) {
              return null
            }
          }
        }

        // Fallback: check raw stack trace string
        if (rawTrace && ALL_FILTER_REGEXES.some((re) => re.test(rawTrace))) {
          return null
        }

        return event
      },
      loaded: () => {
        // Backward compat: TrackingLoader listens for this
        window.dispatchEvent(new Event("posthog-ready"))
      },
    })

    resolvedClient = posthog
    // Ensure window.posthog is set: the npm import path doesn't always do this
    window.posthog = posthog

    // Identify with gtmUpi if available
    const { gtmUpi } = getTrackingCookies()
    if (gtmUpi) {
      posthog.identify(gtmUpi)
    }

    return posthog
  })

  clientPromise.catch((err) => {
    console.error("[posthog] init error", err)
    // Reset so a future call can retry
    clientPromise = null
  })

  return clientPromise
}

// ── Client helper ────────────────────────────────────────────────────

/**
 * Run a callback with the PostHog client. Handles three states:
 * 1. Client already resolved → runs synchronously
 * 2. Client not yet loaded but enabled → queues via ensurePosthogClient()
 * 3. PostHog not enabled for this app → no-ops silently
 */
const withPostHogClient = (run: (client: PostHog) => void) => {
  if (typeof window === "undefined") {
    return
  }
  if (!isPostHogEnabled()) {
    return
  }

  if (resolvedClient) {
    run(resolvedClient)
    return
  }
  if (window.posthog) {
    run(window.posthog)
    return
  }

  // PostHog is enabled but not loaded yet: wait for init
  ensurePosthogClient()
    .then((client) => {
      run(client)
    })
    .catch(() => {
      // ensurePosthogClient already logs and resets internally
    })
}

// ── Track & identify helpers ─────────────────────────────────────────

/**
 * Capture a PostHog event. Safe to call before init: it queues internally
 * via ensurePosthogClient() when PostHog is enabled for the app.
 *
 * Refuses `$exception`. That event must be built by posthog.captureException
 * so `$exception_list` is populated. Manual capture produces malformed events
 * that PostHog ingest rejects.
 */
export const trackPostHog = (name: string, data?: Record<string, unknown>) => {
  if (name === "$exception") {
    console.error(
      "[trackPostHog] Refusing to capture '$exception' manually — " +
        "use posthog.captureException so it carries a proper $exception_list."
    )
    return
  }
  withPostHogClient((client) => {
    client.capture(name, data)
  })
}

/**
 * Report an exception a React error boundary already swallowed.
 *
 * Autocapture only sees exceptions that reach `window.onerror` /
 * `unhandledrejection`. Anything an `error.tsx` boundary catches is "handled"
 * as far as the browser is concerned, so without this call every boundary hit
 * is invisible. Goes through `captureException` so `$exception_list` and the
 * stack trace are built by the SDK.
 *
 * @param error - The error handed to the boundary.
 * @param properties - Extra context merged onto the `$exception` event.
 */
export const capturePostHogException = (
  error: unknown,
  properties?: Record<string, unknown>
) => {
  withPostHogClient((client) => {
    client.captureException(error, properties)
  })
}

/**
 * Identify user in PostHog with optional email/phone.
 */
export const identifyPostHog = ({
  email,
  phone,
}: {
  email?: string | null
  phone?: string | null
}) => {
  const { gtmUpi } = getTrackingCookies()
  if (!gtmUpi) {
    return
  }

  withPostHogClient((client) => {
    client.identify(gtmUpi, {
      email: email ?? undefined,
      phone: phone ?? undefined,
    })
  })
}

/**
 * Register super properties on the PostHog client.
 * Safe to call before init: it queues via withPostHogClient.
 */
export const registerPostHogProperties = (
  properties: Record<string, unknown>
) => {
  withPostHogClient((client) => {
    client.register(properties)
  })
}

/**
 * Posthog component: triggers init via ensurePosthogClient().
 * Redundant in apps that already call it from instrumentation-client.ts
 * (idempotent), but required for apps that only mount StoreTrackingScripts.
 */
export const Posthog = () => {
  if (typeof window !== "undefined") {
    ensurePosthogClient()
  }
  return null
}
